<script lang="ts">
	import type { Currency } from '$lib/components/types';
	import type {
		DateRange,
		PartnerPoiCategory,
		PoiCategory,
		PoiFilterId,
		PoiFilters,
		PoiFilterValue,
	} from '../types';
	import AlertBox from '$lib/components/atoms/alertBox/AlertBox.svelte';
	import Badge from '$lib/components/atoms/badge/Badge.svelte';
	import { usePersistPoiFilters } from '$lib/components/pois/filters/usePersistPoiFilters.svelte';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import {
		trackOpenDatePickers,
		trackValidateDatePickers,
	} from '$lib/services/tracking/googleAnalytics/datepickers';
	import { getSearchParamsStore } from '$lib/services/urls/searchParams';
	import { formatTrackingValue } from '$lib/utils/formatTrackingValue';
	import * as m from '$paraglide/messages';
	import { type CalendarDate } from '@internationalized/date';
	import { createEventDispatcher } from 'svelte';
	import {
		poiCategoryBadgeSchemes,
		poiCategoryIcons,
		poiCategoryTrackerNames,
		poiFilterTrackerNames,
	} from '../constants';
	import DateRangeFilter from './components/DateRangeFilter.svelte';
	import MultiRangeSliderFilter from './components/MultiRangeSliderFilter.svelte';
	import MultiSelectFilter from './components/MultiSelectFilter.svelte';
	import SingleRangeSliderFilter from './components/SingleRangeSliderFilter.svelte';
	import SwitchFilter from './components/SwitchFilter.svelte';
	import FilterSkeleton from './FilterSkeleton.svelte';

	export let poiCategory: Exclude<PoiCategory, PartnerPoiCategory>;
	export let isLoading: boolean;
	export let filters: PoiFilters | null | undefined;
	export let currency: Currency | undefined;

	const dispatch = createEventDispatcher<{ change: void; selectDates: DateRange; clearDates: void }>();
	const searchParams = getSearchParamsStore(false);
	const categoryBorderColorMap: Record<Exclude<PoiCategory, PartnerPoiCategory>, string> = {
		ACCOMMODATION: 'border-hotel-weak',
		RESTAURANT: 'border-restaurant-weak',
		TOURISM: 'border-tourism-weak',
		SERVICE_GAS_STATION: 'border-gas-station-weak',
		EV_SERVICE: 'border-electric-weak',
		PARKING: 'border-parking-weak',
		REST_STOP: 'border-rest-stop-weak',
	};
	const categoryTranslationMap: Record<Exclude<PoiCategory, PartnerPoiCategory>, string> = {
		ACCOMMODATION: m.poi_categories__accommodation(),
		RESTAURANT: m.poi_categories__restaurant(),
		TOURISM: m.poi_categories__tourism(),
		SERVICE_GAS_STATION: m.poi_categories__gas_station(),
		EV_SERVICE: m.poi_categories__ev_service(),
		PARKING: m.poi_categories__parking(),
		REST_STOP: m.poi_categories__rest_stop(),
	};

	$: borderColor = isLoading ? 'border-neutral-soft' : categoryBorderColorMap[poiCategory];
	$: poiFilters = filters ? filters[poiCategory] : [];
	$: accommodationAvailabilityDateRange = $searchParams?.filters?.ACCOMMODATION_AVAILABILITY_DATE_RANGE as DateRange;

	const { setPoiFiltersInStore } = usePersistPoiFilters();

	function handleFilterChange({ id, value }: { id: PoiFilterId; value: PoiFilterValue }) {
		track({
			action: 'panel_advanced_filter',
			payload: {
				action: 'selection',
				poi_category: poiCategoryTrackerNames[poiCategory],
				[poiFilterTrackerNames[id]]: formatTrackingValue(value),
			},
		});

		const searchFilters = $searchParams.filters ?? {};
		const updatedSearchFilters = { ...searchFilters, [id]: value };

		const isArrayAndHasLength = Array.isArray(value) && !value.length;
		const isEmptyValueAndNotBooleanOrNumber
			= ['boolean', 'number'].includes(typeof value) && !value;
		const hasProperty = Reflect.has(updatedSearchFilters, id);

		if (
			(hasProperty && isArrayAndHasLength)
			|| (hasProperty && isEmptyValueAndNotBooleanOrNumber)
		) {
			Reflect.deleteProperty(updatedSearchFilters, id);
		}

		setPoiFiltersInStore(updatedSearchFilters as PoiFilters);
		$searchParams.filters = updatedSearchFilters;

		dispatch('change');
	}

	function handleSelectedDates(datesRange: CalendarDate[]) {
		const startDate = datesRange[0].toString();
		const endDate = datesRange[1].toString();

		if (!startDate || !endDate) {
			return;
		}

		dispatch('selectDates', { startDate, endDate });

		trackValidateDatePickers({
			context: 'advancedFilters',
			poiCategory: poiCategoryTrackerNames.ACCOMMODATION,
			searchParams: $searchParams,
		});
	}

	function handleClickDates(inputId: string) {
		trackOpenDatePickers({
			context: 'advancedFilters',
			inputId,
			poiCategory: poiCategoryTrackerNames.ACCOMMODATION,
			searchParams: $searchParams,
		});
	}
</script>

<div class="bg-neutral-weakest b-2 b-solid {borderColor} rd-2">
	{#if isLoading}
		<FilterSkeleton />
	{:else}
		<div class="flex items-center gap-1 py-4 px-2">
			<Badge icon={poiCategoryIcons[poiCategory]} color={poiCategoryBadgeSchemes[poiCategory]} />
			<span class="text-md text-bold">{categoryTranslationMap[poiCategory]}</span>
		</div>
		<div class="flex flex-col gap-8 py-4 px-2">
			{#each poiFilters as { id, name, filter, notice } (id)}
				<div class="flex flex-col gap-1">
					{#if filter.__typename === 'SwitchFilter'}
						<SwitchFilter
							{id}
							{name}
							defaultValue={filter.switchDefaultValue}
							on:change={({ detail }) => handleFilterChange(detail)}
						/>
					{:else if filter.__typename === 'SingleSliderFilter'}
						<SingleRangeSliderFilter
							min={filter.min}
							max={filter.max}
							defaultValue={filter.singleSliderDefaultValue}
							{name}
							{id}
							on:change={({ detail }) => handleFilterChange(detail)}
							{currency}
						/>
					{:else if filter.__typename === 'MultiSliderFilter'}
						<MultiRangeSliderFilter
							min={filter.min}
							max={filter.max}
							defaultValue={filter.multiSliderDefaultValue}
							{name}
							{id}
							on:change={({ detail }) => handleFilterChange(detail)}
						/>
					{:else if filter.__typename === 'MultiSelectFilter'}
						<MultiSelectFilter
							{name}
							{id}
							defaultValue={filter.multiSelectDefaultValue}
							options={filter.options}
							on:change={({ detail }) => handleFilterChange(detail)}
						/>
					{:else if filter.__typename === 'DateRangeFilter'}
						<DateRangeFilter
							{name}
							{id}
							{accommodationAvailabilityDateRange}
							onOpen={handleClickDates}
							onSelectDates={handleSelectedDates}
							onClearDates={() => dispatch('clearDates')}
						/>
					{/if}
					{#if notice}
						<AlertBox color="neutral" icon="info" text={notice} class="text-coloredneutral" />
					{/if}
				</div>
			{/each}
		</div>
	{/if}
</div>
